import pdfMake from "../../fonts/pdfMakeConfig";
import { viewDocument } from "../utiles/DocumentService";

/* ------------------------------------------------------------------ */
/* Theme – black & white only                                          */
/* ------------------------------------------------------------------ */
const BLACK = "#000000";
const GRAY = "#333333";
const GRID = "#000000";
const HEAD_FILL = "#EEEEEE";

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */
const pickText = (ai, manual) =>
  ai && String(ai).trim() !== "" ? ai : manual || "";
const getObservation = (item) => pickText(item.aiObservation, item.observation);
const getSuggestion = (item) => pickText(item.aiSuggestion, item.suggestion);

const uniq = (arr) =>
  arr.filter(Boolean).filter((v, i, a) => a.indexOf(v) === i);

const sectionHeader = (text) => ({
  text,
  fontSize: 11,
  bold: true,
  color: BLACK,
  margin: [0, 10, 0, 6],
});

const centered = (
  text,
  fontSize = 9,
  bold = true,
  color = BLACK,
  margin = [0, 1, 0, 1],
) => ({
  text,
  alignment: "center",
  fontSize,
  bold,
  color,
  margin,
});

const gridLayout = (
  lineColor = GRID,
  { width = 0.6, padX = 5, padY = 3 } = {},
) => ({
  hLineWidth: () => width,
  vLineWidth: () => width,
  hLineColor: () => lineColor,
  vLineColor: () => lineColor,
  paddingLeft: () => padX,
  paddingRight: () => padX,
  paddingTop: () => padY,
  paddingBottom: () => padY,
});

const headCell = (text, alignment = "center") => ({
  text,
  bold: true,
  fontSize: 9,
  alignment,
  fillColor: HEAD_FILL,
  color: BLACK,
});

export const generateInspectionPDF = async ({
  isQuestion = false,
  orgData,
  auddetails,
  observationdate,
  user,
  empData,
  emptypeOptions,
  details,
  scheduleid,
  introductionContent,
  closingNotes,
}) => {
  const orgcode = localStorage.getItem("custCode") || "ptw";
  const envCode = localStorage.getItem("envcode") || "Prod";

  /* ---------- Report data ---------- */
  const officer = empData.find((d) => d.code === user) || {};
  const inspectionOfficerName = officer.name || user;
  const inspectionOfficerDesignation = officer.designation || "";

  const industry = JSON.parse(auddetails.industry || "{}");
  const occ = JSON.parse(auddetails.occupier || "[]")[0] || {};
  const employees = JSON.parse(auddetails.employee || "[]");

  const employeeTypeMap = Object.fromEntries(
    (emptypeOptions || []).map((opt) => [opt.value, opt.label]),
  );
  const totalMale = employees.reduce((s, e) => s + Number(e.adultMale || 0), 0);
  const totalFemale = employees.reduce(
    (s, e) => s + Number(e.adultFemale || 0),
    0,
  );

  /* ---------- Org header lines (only if present) ---------- */
  const orgName = (orgData?.name || "").trim();
  const orgAddress = uniq([
    orgData?.addressline1,
    orgData?.city,
    orgData?.dist,
    orgData?.state,
    orgData?.country,
    orgData?.pin,
  ]).join(", ");
  const orgContact = uniq([orgData?.contact, orgData?.emailid]).join(" | ");

  const factoryRows = [
    ["Inspection Officer Name", inspectionOfficerName],
    ["Inspection Officer Designation", inspectionOfficerDesignation],
    ["Factory Name", auddetails.name],
    [
      "Factory Address",
      [auddetails.city, auddetails.dist, auddetails.state]
        .filter(Boolean)
        .join(", "),
    ],
    ["License No", auddetails.licencenumber],
    ["Date of Inspection", observationdate],
    ["Occupier Name", industry.occupieName],
    [
      "Occupier Contact & Email",
      [industry.occupierPhone, occ.occupierEmail].filter(Boolean).join(" | "),
    ],
    ["Manager Name", industry.managerName],
    [
      "Manager Contact & Email",
      [industry.managerPhone, industry.managerEmail]
        .filter(Boolean)
        .join(" | "),
    ],
    [
      "Management Personnel present at the time of inspection",
      industry.managementper,
    ],
  ];

  // Turns plain text (or simple HTML from an editor) into PDF paragraphs
  const textBlocks = (value) => {
    const raw = String(value ?? "")
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/(p|div|li)>/gi, "\n")
      .replace(/<[^>]+>/g, "")
      .replace(/&nbsp;/g, " ")
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .trim();

    if (!raw) return [];

    return raw
      .split(/\n+/)
      .map((p) => p.trim())
      .filter(Boolean)
      .map((p) => ({
        text: p,
        fontSize: 9,
        color: BLACK,
        alignment: "justify",
        margin: [0, 0, 0, 4],
      }));
  };

  /* ---------- Server image helpers ---------- */
  const fetchLogo = async () => {
    if (!orgData?.logo) return null;
    try {
      const response = await viewDocument({
        projectCode: "Ptw",
        envCode,
        folderPath: `${orgcode}/org_${orgData.code}`,
        fileName: orgData.logo,
      });
      if (!response || response?.cd === 1) return null;

      const mime = orgData.logo.toLowerCase().endsWith(".pdf")
        ? "application/pdf"
        : "image/jpeg";
      const url = `data:${mime};base64,${response}`;
      return url.startsWith("data:image/") ? url : null;
    } catch (err) {
      console.error("Logo fetch error:", err);
      return null;
    }
  };

  const fetchObservationImages = async (imageNames = []) => {
    if (!Array.isArray(imageNames) || imageNames.length === 0) return [];

    const results = [];
    for (const fileName of imageNames) {
      try {
        const response = await viewDocument({
          projectCode: "Ptw",
          envCode,
          folderPath: `${orgcode}/sc_${scheduleid}`,
          fileName,
        });
        if (!response || response?.cd === 1) {
          results.push(null);
          continue;
        }

        const lower = fileName.toLowerCase();
        let mime = "image/png";
        if (lower.endsWith(".jpg") || lower.endsWith(".jpeg"))
          mime = "image/jpeg";
        if (lower.endsWith(".gif")) mime = "image/gif";

        results.push(`data:${mime};base64,${response}`);
      } catch (err) {
        console.error("Image fetch error for:", fileName, err);
        results.push(null);
      }
    }
    return results;
  };

  const isValidImage = (img) =>
    typeof img === "string" && img.startsWith("data:image/") && img.length > 30;

  const logoDataUrl = await fetchLogo();

  /* ---------- Letter-head: org details only (if present) ---------- */
  const letterHead = [
    ...(logoDataUrl
      ? [
          {
            image: logoDataUrl,
            width: 60,
            alignment: "center",
            margin: [0, 0, 0, 4],
          },
        ]
      : []),
    ...(orgName ? [centered(orgName, 13, true, BLACK)] : []),
    ...(orgAddress ? [centered(orgAddress, 9, false, GRAY)] : []),
    ...(orgContact ? [centered(orgContact, 8, false, GRAY)] : []),
    {
      canvas: [
        {
          type: "line",
          x1: 0,
          y1: 0,
          x2: 531,
          y2: 0,
          lineWidth: 1.2,
          lineColor: BLACK,
        },
      ],
      margin: [0, 6, 0, 8],
    },
  ];

  /* ---------- Observations as paragraphs (no bullets) ---------- */
  const buildObservations = async () => {
    const blocks = [];
    let counter = 1;

    for (const item of details || []) {
      const obsText = getObservation(item);
      const sugText = getSuggestion(item);
      if (obsText.trim() === "" && sugText.trim() === "") continue;

      const stack = [];

      if (isQuestion && item.question) {
        // Question mode: show question as heading and observation answer
        stack.push({
          text: `${counter}) ${item.question}`,
          bold: true,
          fontSize: 10,
          color: BLACK,
          margin: [0, 8, 0, 4],
        });

        if (obsText.trim()) {
          stack.push({
            text: obsText.trim(),
            fontSize: 9,
            color: BLACK,
            alignment: "justify",
            margin: [0, 0, 0, 4],
          });
        }
      } else {
        // Non-question mode: observation is the heading
        stack.push({
          text: `${counter}) ${obsText}`,
          bold: true,
          fontSize: 10,
          color: BLACK,
          margin: [0, 8, 0, 4],
        });
      }

      // Show suggestion directly after the heading/content
      if (sugText.trim()) {
        stack.push({
          text: sugText.trim(),
          fontSize: 9,
          color: BLACK,
          alignment: "justify",
          margin: [0, 0, 0, 4],
        });
      }

      blocks.push({
        unbreakable: true,
        stack,
      });

      const images = (await fetchObservationImages(item.image || [])).filter(
        isValidImage,
      );
      images.forEach((img) =>
        blocks.push({ image: img, width: 130, margin: [0, 3, 0, 5] }),
      );

      counter++;
    }

    if (counter === 1) {
      return [
        {
          text: "No observations recorded.",
          italics: true,
          color: GRAY,
          margin: [0, 4, 0, 10],
        },
      ];
    }
    return blocks;
  };

  /* ---------- Document definition ---------- */
  const docDefinition = {
    pageSize: "A4",
    pageMargins: [32, 24, 32, 45],
    defaultStyle: { font: "Poppins", fontSize: 9, color: BLACK },

    background: (currentPage, pageSize) => ({
      canvas: [
        {
          type: "rect",
          x: 12,
          y: 12,
          w: pageSize.width - 24,
          h: pageSize.height - 24,
          lineWidth: 1.2,
          lineColor: BLACK,
        },
      ],
    }),

    footer: (currentPage, pageCount) => ({
      text: `Page ${currentPage} of ${pageCount}`,
      alignment: "center",
      fontSize: 8,
      color: GRAY,
      margin: [0, 8, 0, 0],
    }),

    content: [
      ...letterHead,
      {
        text: "Inspection Report",
        alignment: "center",
        fontSize: 13,
        bold: true,
        color: BLACK,
        margin: [0, 4, 0, 8],
      },

      sectionHeader("1) Factory Details"),
      {
        table: {
          widths: [180, "*"],
          body: factoryRows.map(([label, value]) => [
            { text: label, bold: true, fontSize: 9, color: BLACK },
            { text: value || "", fontSize: 9, color: BLACK },
          ]),
        },
        layout: gridLayout(BLACK),
      },

      sectionHeader("2) Employees Summary"),
      {
        table: {
          widths: ["*", 90, 90],
          body: [
            [
              headCell("Employee Type", "left"),
              headCell("Adult Male"),
              headCell("Adult Female"),
            ],
            ...employees.map((e) => [
              {
                text:
                  employeeTypeMap[e.employeeType] || `Type ${e.employeeType}`,
                fontSize: 9,
                color: BLACK,
              },
              {
                text: String(e.adultMale || 0),
                alignment: "center",
                fontSize: 9,
                color: BLACK,
              },
              {
                text: String(e.adultFemale || 0),
                alignment: "center",
                fontSize: 9,
                color: BLACK,
              },
            ]),
            [
              { text: "Total", bold: true, fontSize: 9, color: BLACK },
              {
                text: String(totalMale),
                bold: true,
                alignment: "center",
                fontSize: 9,
                color: BLACK,
              },
              {
                text: String(totalFemale),
                bold: true,
                alignment: "center",
                fontSize: 9,
                color: BLACK,
              },
            ],
          ],
        },
        layout: gridLayout(BLACK, { width: 0.5, padX: 4, padY: 2 }),
      },

      ...(textBlocks(introductionContent).length
        ? [
            sectionHeader("3) Opening Notes"),
            ...textBlocks(introductionContent),
          ]
        : []),

      sectionHeader(
        "4) Inspection Observations and Suggested Corrective Actions",
      ),
      ...(await buildObservations()),

      // Closing Notes - Always last among report content sections
      ...(textBlocks(closingNotes).length
        ? [sectionHeader("5) Closing Notes"), ...textBlocks(closingNotes)]
        : []),

      {
        margin: [0, 30, 0, 10],
        unbreakable: true,
        columns: [
          {
            width: "50%",
            stack: [
              {
                text: "Place: ________________________",
                bold: true,
                color: BLACK,
                margin: [0, 0, 0, 14],
              },
              {
                text: "Date of issue: ________________",
                bold: true,
                color: BLACK,
              },
            ],
          },
          {
            width: "50%",
            stack: [
              {
                text: "Signature: ____________________________",
                bold: true,
                color: BLACK,
                margin: [0, 0, 0, 14],
              },
              {
                text: "Name: ________________________________",
                bold: true,
                color: BLACK,
              },
            ],
          },
        ],
      },

      /* Separate designation table */
      {
        margin: [0, 4, 0, 0],
        table: {
          widths: ["50%", "50%"],
          body: [
            [
              {
                text: "Additional Director of Factories and Boilers",
                alignment: "left",
                bold: true,
                fontSize: 9,
                color: BLACK,
                border: [false, false, false, false],
                margin: [0, 0, 0, 0],
              },
              { text: "", border: [false, false, false, false] },
            ],
          ],
        },
        layout: "noBorders",
      },
    ],
  };

  return new Promise((resolve, reject) => {
    try {
      pdfMake.createPdf(docDefinition).getBlob((blob) => resolve(blob));
    } catch (err) {
      console.error("PDF generation failed:", err);
      reject(err);
    }
  });
};
